import { FaGalacticRepublic } from "react-icons/fa";
import { useState } from 'react';

// Utility function to convert strings to title case
export const convertCase = (str) => {
  return str
    .replace(/_/g, ' ')  // Replace underscores with spaces
    .replace(/\b\w/g, match => match.toUpperCase()); // Capitalize the first letter of each word
};

export const getUniqueFilteredData = (data, header) => {
  return data.filter((value, index, self) => {
    return index === self.findIndex((t) => t[header] === value[header]);
  });
};

export const formatSize = (size) => {
  if (size.includes('"')) {
    return size.replace(/\"/, ' inch');
  } else {
    return `${size} inch`;
  }
};

export const validateDecimal = (value) => {
  const decimalPattern = /^\d{1,10}(\.\d{0,5})?$/;
  if (value.startsWith('0') && value.length > 1) {
    return false;
  }
  return decimalPattern.test(value);
};

export const validateInteger = (value) => {
  const integerPattern = /^\d{1,10}$/;
  return integerPattern.test(value);
};

export const validateSalesInteger = (value) => {
  // Regular expression to match integers (with optional decimals)
  const integerPattern = /^[1-9][0-9]{0,9}$/; // Match digits from 1-9 and allow up to 10 digits

  // Allow 0 followed by a decimal (0.)
  if (value === '0.') {
    return true;
  }

  return integerPattern.test(value);
};

export const commonFilter = (data, filterHeader, searchValue) => {
  // Normalize the search value to lowercase for case-insensitive comparison
  console.log("data",data,filterHeader,searchValue);
  
  const normalizedSearchValue = searchValue.toLowerCase();

  const filtered = data?.filter(item => {
    // If filterHeader is provided, normalize it to lowercase
    const formatheader = filterHeader ? filterHeader.toLowerCase() : null;

    // If filterHeader is provided, check if the item contains the matching key
    if (formatheader) {
      const itemKeys = Object.keys(item);
      const matchingKey = itemKeys.find(key => key.toLowerCase() === formatheader);

      if (matchingKey) {
        const itemValue = item[matchingKey];

        // Handle number and string comparison
        if (typeof itemValue === 'number') {
          return itemValue.toString().includes(searchValue.toString());
        }

        if (typeof itemValue === 'string') {
          return itemValue.toLowerCase().includes(normalizedSearchValue);
        }
      }

      return false;
    }

    // If no filterHeader is provided, search across all keys in the item
    return Object.keys(item).some(key => {
      const itemValue = item[key];

      // Handle number and string comparison
      if (typeof itemValue === 'number') {
        return itemValue.toString().includes(searchValue.toString());
      }

      if (typeof itemValue === 'string') {
        return itemValue.toLowerCase().includes(normalizedSearchValue);
      }

      return false; // For other types (objects, arrays, etc.), exclude them
    });
  });

  return filtered;
}

export const validateDecimalInput = (values) => {
  // Convert value to string if it's not already
  values = values ? values.toString() : '';

  // Remove any characters that are not digits, decimal point, or hyphen
  values = values.replace(/[^0-9.]/g, '');
  if (values.startsWith('.') || values.startsWith('-')) {
    values = '0' + values; // Prepend '0' if it starts with a decimal or hyphen
  }

  // Ensure that the decimal point appears only after at least two digits
  if (values.indexOf('.') !== -1) {
    const integerPart = values.split('.')[0];

    // Only allow a decimal point if there are at least two digits before it
    if (integerPart.length < 2) {
      values = values.replace('.', ''); // Remove the decimal if there are less than two digits before it
    } else {
      // Ensure only one decimal point exists
      if (values.indexOf('.') !== values.lastIndexOf('.')) {
        values = values.substring(0, values.indexOf('.') + 1) + values.split('.').slice(1).join('');
      }
    }
  }

  const regex = /^\d{1,10}(\.\d{0,5})?$/;

  // If the value doesn't match the regex, it's invalid
  if (!regex.test(values)) {
    // We want to trim the value to meet the constraints of the regex
    const [integerPart, decimalPart] = values.split('.');

    // Limit the integer part (before decimal) to 10 digits
    const limitedIntegerPart = integerPart.slice(0, 10);

    // Limit the decimal part (after decimal) to 5 digits
    const limitedDecimalPart = decimalPart ? decimalPart.slice(0, 5) : '';

    // Combine the parts back
    values = `${limitedIntegerPart}${limitedDecimalPart ? '.' + limitedDecimalPart : ''}`;
  }

  // Ensure only one decimal point is present
  if (values.indexOf('.') !== values.lastIndexOf('.')) {
    values = values.substring(0, values.indexOf('.') + 1) + values.split('.').slice(1).join('');
  }

  // Prevent invalid starts (like starting with decimal or hyphen)
  if (values.startsWith('.')) {
    values = '0' + values; // Prepend '0' if it starts with a decimal or hyphen
  }

  // Check if values is a valid decimal using regex
  const decimalRegex = /^-?\d*\.?\d*$/;
  if (!decimalRegex.test(values)) {
    return { isValid: false, error: 'Invalid decimal format' };
  }

  // If values starts with 0, and is not just "0" or "0." (like "0123" or "01.23")
  if (values.startsWith('0') && values !== '0' && !values.startsWith('0.')) {
    values = values.slice(1);  // Remove the leading '0'
  }
  // Return the cleaned values if valid
  return { isValid: true, values };
};

const validateField = (validationType, data, field, formData) => {
  const value = formData[field];

  if (validationType === 'exists') {
    // Check if the field value exists in the data array
    return data.some(item => item[field] === value);
  } else if (validationType === 'required') {
    // Check if the field value is non-empty, non-null, and non-zero
    return value !== undefined && value !== null && value !== '' && value !== 0;
  }

  return true;
};

// Unified dynamic validation function for all types of validation
export const validateFormData = (validationRules, data, formData) => {
  let newErrorMessage = {};

  validationRules.forEach(({ field, validationTypes }) => {
    validationTypes.forEach(validationType => {
      const isValid = validateField(validationType, data, field, formData);

      if (!isValid) {
        if (validationType === 'exists') {
          newErrorMessage[field] = `${formData[field]} does not exist in the system.`;
        } else if (validationType === 'required') {
          newErrorMessage[field] = `${field} is required.`;
        }
      }
    });
  });

  return newErrorMessage;
};

export const mapDeliveryType = (type) => {
  switch (type) {
    case 'GI_Pipes':
      return 'PIPE';
    case 'GI_SmallBox':
      return 'SMALL';
    case 'GI_BigBox':
      return 'BIG';
      case 'GI_Purlin':
        return 'PURLIN';
    default:
      return type; 
  }
};

export const getImagePath = (type) => {
  switch (type) {
    case "GI_Pipes":
      return "/Pipes.png";
    case "GI_SmallBox":
      return "/Square.png";
    case "GI_BigBox":
      return "/Rectangle.png";
      case "GI_Purlin":
        return "/Purlin.png";
    default:
      return "";
  }
};


// Helper: convert single part like "1", "3/4", "1 1/2" to number
function parseFraction(str) {
  if (!str.includes("/")) {
    return parseFloat(str);
  }
  const [numerator, denominator] = str.split("/");

  if (numerator.length === 2) {
    // e.g. "11/2" => 1 + 1/2
    const integerPart = parseInt(numerator[0], 10);
    const fractionNumerator = parseInt(numerator[1], 10);
    const fractionDenominator = parseInt(denominator, 10);
    return integerPart + fractionNumerator / fractionDenominator;
  } else if (numerator.length === 1) {
    // e.g. "1/2"
    return parseInt(numerator, 10) / parseInt(denominator, 10);
  } else {
    // fallback for any weird cases
    return parseInt(numerator, 10) / parseInt(denominator, 10);
  }
}

// Parse size string like "3 X 11/2"
function parseSize(sizeStr) {
  const parts = sizeStr.split("X").map((s) => s.trim());
  return parts.map(parseFraction);
}

// Compare sizes lexicographically by numeric parts
export function compareSizes(a, b) {
  const aParts = parseSize(a);
  const bParts = parseSize(b);

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aVal = aParts[i] || 0;
    const bVal = bParts[i] || 0;
    if (aVal !== bVal) return aVal - bVal;
  }
  return 0;
}
// Example: formatSize("1 1/2") -> 1.5

export const generateQRData = (inputData, page) => {
  if (!inputData) {
    console.warn("No input data provided.");
    return null; // or undefined, or throw an error based on your use case
  }
  // const currentDate = new Date().toLocaleDateString();
console.log("inputData",inputData,page);

  return {
    
      // PONumber: JSON.stringify(inputData.pur_Number ? inputData.pur_Number || 0 : inputData.order_Num) || "0",
      PONumber:  JSON.stringify(Number(inputData.Order_Number ? inputData.Order_Number : 0)) ,
      Itemcode: inputData.item_Code,
      ItemName: inputData.item_Name,
      BatchNumber: inputData.batch_Number,
      ActualWt: inputData.Actual_Weight,
      CreatedDate: inputData.CreatedDate,
      ...(page === "GRPO" && { QtyNos: inputData.QtyNos }),
  };
};
 const keyMap = {
  ponumber: "PONumber",
  itemcode: "Itemcode",
  itemname: "ItemName",
  batchnumber: "BatchNumber",
  actualwt: "ActualWt",
  createddate: "CreatedDate",
};

export const parseFormattedQRText = (text) => {
  const lines = text.split("\n");
  const obj = {};
  for (const line of lines) {
    const [key, ...rest] = line.split(":");
    if (key && rest.length > 0) {
      const normalizedKey = key.trim().toLowerCase().replace(/ /g, "");
      const mappedKey = keyMap[normalizedKey] || normalizedKey;
      obj[mappedKey] = rest.join(":").trim();
    }
  }
  return obj;
};

// export const generateBatchNumber = (item, lineId) => {
//   const currentDate = new Date();
//   const day = String(currentDate.getDate()).padStart(2, "0");
//   const year = String(currentDate.getFullYear()).slice(-2);
//   const formattedDate = `${day}${year}`;

//   const batchNo = `${item.item_Code}/${item.docNum}DA${formattedDate}${lineId}`;
//   return batchNo;
// };
export const generateBatchNumber = (item, lineId) => {
  const currentDate = new Date();
  // Day
  const day = String(currentDate.getDate()).padStart(2, "0");
  // Month (JAN, FEB...)
  const month = currentDate
    .toLocaleString("en-US", { month: "short" })
    .toUpperCase();
  // Year → Alphabet (A = 2026)
  const baseYear = 2025;
  const year = currentDate.getFullYear();
  const yearAlpha = String.fromCharCode(65 + (year - baseYear));
  // Final batch number
  const batchNo = `${item.item_Code}/${item.docNum}${month}${day}${yearAlpha}${lineId}`;
  return batchNo;
};
export function sortItemsByHeightAndLength(items) {
  return items.slice().sort((a, b) => {
    const heightDiff = a.height - b.height;
    if (heightDiff !== 0) return heightDiff;

    // Extract numeric length from the end of item_Name (e.g., "3.048M")
    const extractLength = (name) => {
      const match = name.match(/(\d+(\.\d+)?)M$/);
      return match ? parseFloat(match[1]) : 0;
    };

    const lengthA = extractLength(a.item_Name);
    const lengthB = extractLength(b.item_Name);

    return lengthA - lengthB;
  });
}

export const getAttachmentPayloads = async (fileDataArray) => {
  if (!Array.isArray(fileDataArray)) return [];

  const payloads = await Promise.all(
    fileDataArray.map(async (fileData) => {
      if (!fileData?.file) return null;

      const base64 = await fileToBase64(fileData.file);
      return {
        attachmentFileName: fileData.file.name,
        attachmentFileBytes: base64
      };
    })
  );

  // Remove any nulls (in case of missing files)
  return payloads.filter(p => p !== null);
};
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const base64String = reader.result.split(',')[1]; // strip `data:...base64,`
      resolve(base64String);
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
};